import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import lodashCompact from 'lodash/fp/compact';
import lodashUniq from 'lodash/fp/uniq';
import PropTypes from 'prop-types';

import { SR_API_ACTION_UTILITY_PROVIDER_UPSERT } from 'api/utilityActionTypes';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';
import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';

import { showFieldIf } from 'client/helpers/formikHelpers';

import {
  UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER,
  UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
  UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED,
  UTILITY_COVERAGE_POINT_VERIFICATION_METHOD_EDITABLE_LIST,
  UTILITY_COVERAGE_POINT_VERIFICATION_METHOD_NEARBY_PROVIDER,
  UTILITY_COVERAGE_POINT_VERIFICATION_METHOD_OTHER,
  UTILITY_PROVIDER_SOURCE_PARTNER,
  UTILITY_PROVIDER_SOURCE_STAFF,
  UTILITY_PROVIDER_SOURCE_SYSTEM,
  UTILITY_PROVIDER_SOURCE_TENANT,
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
} from 'common/utility/constants';

import { makeFullAddressDisplay } from '@utilityprofit/address-helpers';

import { getActivationItemStatusUiState } from 'common/utility/helpers';

import SrButton, {
  SR_BUTTON_HEIGHT_SHORT,
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_ORANGE,
} from 'client/components/SrButton/SrButton';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import { formatWithTwoDecimals } from 'common/formatters';
import firstWordCapitalized from 'common/util/firstWordCapitalized';

import SrBox from 'client/components/SrBox';
import SrExternalLink from 'client/components/SrExternalLink';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrUtilityProfitAdminProcessAddressProviderListForm from 'client/components/SrUtilityProfitAdminProcessAddressPage/SrUtilityProfitAdminProcessAddressProviderListForm';
import { UPProviderSearchFormInner } from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';

const SOURCE_COLOR_MAPPING = {
  [UTILITY_PROVIDER_SOURCE_PARTNER]: 'purpleRoyal',
  [UTILITY_PROVIDER_SOURCE_STAFF]: 'gold',
  [UTILITY_PROVIDER_SOURCE_TENANT]: 'green',
  [UTILITY_PROVIDER_SOURCE_SYSTEM]: 'gold',
};

const SOURCE_NAME_MAPPING = {
  [UTILITY_PROVIDER_SOURCE_PARTNER]: 'Partner',
  [UTILITY_PROVIDER_SOURCE_STAFF]: 'Admin',
  [UTILITY_PROVIDER_SOURCE_TENANT]: 'Tenant',
  [UTILITY_PROVIDER_SOURCE_SYSTEM]: 'Admin',
};

const formFields = {
  serviceArea: {
    fields: ({
      item,
      addressId,
      utilityProvidersByAddress,
      isServiceAreaLoading,
      setServiceAreaLoading,
      serviceAreaLoadingOptionId,
      setServiceAreaLoadingOptionId,
      updateProviderCoveragePoints,
      onMapServiceAreaProvider,
      formikBag,
    }) => [
      {
        title: 'Coverage Map Providers',
        id: 'utilityProviderIdsInServiceArea',
        type: 'multiple_choice',
        properties: {
          onChange: async (newOptions, optionId) => {
            if (isServiceAreaLoading) {
              return;
            }

            const utilityServiceAreaId =
              optionId && optionId.includes('serviceArea-')
                ? parseInt(optionId.replace('serviceArea-', ''), 10)
                : null;

            const providerId = optionId && !isNaN(optionId) ? parseInt(optionId, 10) : null;

            if (utilityServiceAreaId) {
              await onMapServiceAreaProvider({
                addressId,
                utilityServiceAreaId,
                utilityTypeId: item.type.id,
                companyName: item.utilityProvidersInServiceAreas.find(({ id }) => {
                  return id === utilityServiceAreaId;
                }).name,
              });
            } else {
              const existingUtilityProviderIds = item.availableProviders.map(({ id }) => id);
              setServiceAreaLoading(true);
              setServiceAreaLoadingOptionId(optionId);
              try {
                if (existingUtilityProviderIds.includes(providerId)) {
                  // Remove utility provider
                  await updateProviderCoveragePoints({
                    addressId: addressId,
                    utilityTypeId: item.type.id,
                    utilityProviderIds: existingUtilityProviderIds.filter(
                      (id) => id !== providerId,
                    ),
                  });
                } else {
                  // Add utility provider
                  await updateProviderCoveragePoints({
                    addressId: addressId,
                    utilityTypeId: item.type.id,
                    utilityProviderIds: lodashUniq([...existingUtilityProviderIds, providerId]),
                  });
                }
              } finally {
                setServiceAreaLoading(false);
                setServiceAreaLoadingOptionId(null);
              }

              formikBag.resetForm();
            }
          },
          choices: item.utilityProvidersInServiceAreas.map(
            ({ id, utilityProviderId, utilityProviderTitle, name }) => {
              const mappedProvider = item.availableProviders.find(
                ({ id: availableProviderId }) => availableProviderId === utilityProviderId,
              );
              const providerByAddress = utilityProvidersByAddress.find(
                ({ id: providerId }) => providerId === utilityProviderId,
              );

              return {
                label: name,
                sublabel:
                  mappedProvider?.title ||
                  providerByAddress?.title ||
                  utilityProviderTitle ||
                  'Connect',
                sublabelClassName: 'blueGreycliff',
                value: utilityProviderId ? String(utilityProviderId) : `serviceArea-${id}`,
                isLoading:
                  isServiceAreaLoading &&
                  String(serviceAreaLoadingOptionId) ===
                    String(utilityProviderId ? String(utilityProviderId) : `serviceArea-${id}`),
              };
            },
          ),
          disabled: isServiceAreaLoading,
          multipleSelection: true,
          allowEmptyArray: true,
          selectedItemsFirst: true,
          checkboxListClassName: `checkboxListProvider brown${
            isServiceAreaLoading ? ' loading' : ''
          }`,
          checkboxClassName: 'checkboxProvider',
        },
      },
    ],
    initialState: ({ item }) => ({
      utilityProviderIdsInServiceArea: item.availableProviders.map(({ id }) => String(id)),
    }),
  },
  admin: {
    fields: ({
      item,
      adminProviders,
      utilityProvidersByAddress,
      formikBag,
      requiredTypes,
      utilityType,
      onConfirmMapping,
    }) => {
      const requiredUtilityType = requiredTypes.find(
        ({ utilityTypeId }) => utilityTypeId === utilityType.id,
      );

      const formFields = [
        {
          id: 'coverageUtilityProviderIds',
          title: 'Select the available providers',
          type: 'multiple_choice',
          properties: {
            choices: adminProviders.map(({ title, id, source, sublabel }) => ({
              label: title,
              sublabel,
              sublabelClassName: SOURCE_COLOR_MAPPING[source] || 'blueGreycliff',
              value: id,
            })),
            multipleSelection: true,
            allowEmptyArray: true,
            selectedItemsFirst: true,
            selectedItemsOnly: false,
            checkboxListClassName: 'checkboxListProvider',
            checkboxClassName: 'checkboxProvider',
            widget: requiredUtilityType.needsMappingConfirmation ? (
              <SrBox padding="16px 0">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_ORANGE}
                  heightType={SR_BUTTON_HEIGHT_SHORT}
                  minWidth={null}
                  onClick={() => {
                    onConfirmMapping({
                      addressId: requiredUtilityType.addressId,
                      utilityTypeId: utilityType.id,
                    });
                  }}
                >
                  {utilityType.id === UTILITY_TYPE_ID_INTERNET
                    ? 'I confirm only Satellite or Copper is available'
                    : 'I confirm the mapping is correct'}
                </SrButton>
              </SrBox>
            ) : null,
          },
        },
      ];

      formFields.push(
        ...(formikBag.values?.coverageUtilityProviderIds || []).reduce((memo, providerId) => {
          const providerIdAsNumber = parseInt(providerId, 10);
          const utilityProviderSelected = adminProviders.find(({ id }) => {
            return id === providerIdAsNumber;
          });
          const availableProvider = item.availableProviders.find(
            ({ id }) => id === providerIdAsNumber,
          );
          const providerByAddress = utilityProvidersByAddress.find(
            ({ id }) => id === providerIdAsNumber,
          );
          const serviceAreaProvider = item.utilityProvidersInServiceAreas.find(
            ({ utilityProviderId }) => parseInt(utilityProviderId, 10) === providerIdAsNumber,
          );
          const providerName =
            availableProvider?.title ||
            providerByAddress?.title ||
            serviceAreaProvider?.utilityProviderTitle ||
            utilityProviderSelected?.title ||
            providerId;
          const coveragePointConfig =
            formikBag.values.coveragePointsConfigByProviderId[providerId] || {};

          return [
            ...memo,
            coveragePointConfig.editingVerificationLink
              ? {
                  id: `coveragePointsConfigByProviderId[${providerId}].addressVerificationLink`,
                  title: `Verification Link for "${providerName}"`,
                  type: 'short_text',
                  validations: ['url'],
                }
              : {
                  id: `coveragePointsConfigByProviderId[${providerId}].addressVerificationLink`,
                  title: `Verification Methods for "${providerName}"`,
                  type: 'dom',
                  properties: {
                    dom: (
                      <>
                        <SrText
                          font="greycliff"
                          size="16"
                          weight="500"
                          cursor="pointer"
                          onClick={() => {
                            formikBag.setFieldValue(
                              `coveragePointsConfigByProviderId[${providerId}].editingVerificationLink`,
                              true,
                            );
                          }}
                        >
                          {`Verification Link: ${
                            coveragePointConfig.addressVerificationLink ? '' : 'No link'
                          }`}
                        </SrText>
                        {coveragePointConfig.addressVerificationLink && (
                          <SrExternalLink link={coveragePointConfig.addressVerificationLink}>
                            <SrText font="greycliff" weight="800" color="blue300" cursor="pointer">
                              {coveragePointConfig.addressVerificationLink}
                            </SrText>
                          </SrExternalLink>
                        )}
                      </>
                    ),
                  },
                },
            {
              id: `coveragePointsConfigByProviderId[${providerId}].verificationMethods`,
              title: coveragePointConfig.editingVerificationLink
                ? `Verification Methods for "${providerName}"`
                : '',
              type: 'multiple_choice',
              properties: {
                choices: UTILITY_COVERAGE_POINT_VERIFICATION_METHOD_EDITABLE_LIST.map((value) => ({
                  label: camelCaseToTitleCase(value),
                  value: value,
                })),
                multipleSelection: true,
                allowEmptyArray: true,
                checkboxListClassName: 'checkboxListProvider',
                checkboxClassName: 'checkboxProvider',
              },
            },
            showFieldIf(
              (coveragePointConfig.verificationMethods || []).includes(
                UTILITY_COVERAGE_POINT_VERIFICATION_METHOD_OTHER,
              ),
              {
                id: `coveragePointsConfigByProviderId[${providerId}].verificationMethodOtherExplanation`,
                title: '"Other" Verification Method Explanation',
                type: 'long_text',
                properties: {
                  placeholder: 'Write here...',
                  style: {
                    height: '90px',
                  },
                },
                validations: ['required'],
              },
            ),
            {
              id: `coveragePointsConfigByProviderId[${providerId}].editingVerificationLink`,
              type: 'hidden',
            },
          ];
        }, []),
      );

      formFields.push(
        formikBag.values.editingMappingNotes
          ? {
              id: 'mappingNotes',
              title: 'Mapping notes',
              type: 'long_text',
              properties: {
                placeholder: 'Write here...',
                style: {
                  height: '90px',
                },
              },
            }
          : {
              id: 'mappingNotes',
              title: 'Mapping notes',
              type: 'dom',
              properties: {
                dom: (
                  <>
                    <SrText
                      font="greycliff"
                      size="16"
                      weight="500"
                      cursor="pointer"
                      onClick={() => {
                        formikBag.setFieldValue('editingMappingNotes', true);
                      }}
                    >
                      {formikBag.values.mappingNotes || '--'}
                    </SrText>
                  </>
                ),
              },
            },
      );

      return formFields;
    },
    initialState: ({ item, requiredTypes }) => {
      const requiredType = requiredTypes.find(
        ({ utilityTypeId }) => utilityTypeId === item.type.id,
      );
      const coverageUtilityProviderIds = item.availableProviders.map(({ id }) => id);
      const coveragePointsConfigByProviderId = item.availableProviders.reduce(
        (memo, provider) => ({
          ...memo,
          [provider.id]: {
            addressVerificationLink: provider.addressVerificationLink,
            verificationMethods: provider.coveragePoint.verificationMethods,
            verificationMethodOtherExplanation:
              provider.coveragePoint.verificationMethodOtherExplanation,
          },
        }),
        {},
      );

      return {
        mappingNotes: requiredType?.mappingNotes || '',
        editingMappingNotes: false,
        coverageUtilityProviderIds,
        coveragePointsConfigByProviderId,
      };
    },
    onSubmit:
      ({ addressId, item, updateProviderCoveragePoints }) =>
      (formValues) => {
        const formValuesCoveragePoints = Object.keys(
          formValues.coveragePointsConfigByProviderId || {},
        );
        const mappingNotes = formValues.mappingNotes || null;

        const coveragePointsConfig = formValuesCoveragePoints.reduce((memo, providerIdStr) => {
          const utilityProviderId = parseInt(providerIdStr, 10);

          if (formValues.coverageUtilityProviderIds?.includes(utilityProviderId)) {
            const providerConfig = formValues.coveragePointsConfigByProviderId[providerIdStr];
            const includesOther = providerConfig.verificationMethods.includes(
              UTILITY_COVERAGE_POINT_VERIFICATION_METHOD_OTHER,
            );

            if (!includesOther || !providerConfig.verificationMethodOtherExplanation) {
              providerConfig.verificationMethodOtherExplanation = null;
            }

            memo.push({
              utilityProviderId,
              addressVerificationLink: providerConfig.addressVerificationLink,
              updates: {
                verificationMethods: providerConfig.verificationMethods,
                verificationMethodOtherExplanation:
                  providerConfig.verificationMethodOtherExplanation,
              },
            });
          }
          return memo;
        }, []);

        return updateProviderCoveragePoints({
          addressId,
          utilityTypeId: item.type.id,
          utilityProviderIds: formValues.coverageUtilityProviderIds,
          coveragePointsConfig,
          mappingNotes,
        });
      },
  },
};

const getProviderProps = ({ providers, mappedProviders = [], type }) => {
  return providers
    .filter(
      (provider) => !mappedProviders.some(({ id }) => id === provider.id && type === 'nearby'),
    )
    .map((provider) => ({
      id: provider.id,
      title: provider.title,
      addressVerificationLink: provider.addressVerificationLink,
      source: SOURCE_NAME_MAPPING[provider.source] || firstWordCapitalized(provider.source),
      sourceColor: SOURCE_COLOR_MAPPING[provider.source] || 'blueGreycliff',
      isMapped: mappedProviders.some(({ id }) => id === provider.id),
      url: provider.url,
      ...(type === 'nearby' && {
        distanceInMiles:
          provider.distanceInMiles < 0.01
            ? '< 0.01'
            : formatWithTwoDecimals(provider.distanceInMiles),
      }),
    }));
};

const getSections = ({
  addressId,
  items,
  requiredTypes,
  utilityTypes,
  utilityProvidersByTypeId,
  utilityProvidersInRangeByTypeId,
  utilityProvidersByAddressByTypeId,
  updateProviderCoveragePoints,
  collapseProviders,
  setCollapseProviders,
  onMapServiceAreaProvider,
  hideInactiveTypes,
  collapsible,
  collapsibleState,
  setCollapsibleState,
  onConfirmMapping,
  serviceAreaLoadingByTypeId = {},
  setServiceAreaLoadingByTypeId,
  serviceAreaLoadingOptionIdByTypeId = {},
  setServiceAreaLoadingOptionIdByTypeId,
}) => {
  let sections = utilityTypes.map((type) => {
    const item = items.find((item) => item.type.id === type.id);
    const requiredUtilityType = requiredTypes.find(
      ({ utilityTypeId }) => utilityTypeId === type.id,
    );
    const utilityProvidersInRange = utilityProvidersInRangeByTypeId[type.id] || [];
    const utilityProvidersByAddress = utilityProvidersByAddressByTypeId[type.id] || [];
    const isServiceAreaLoading = !!serviceAreaLoadingByTypeId[type.id];
    const serviceAreaLoadingOptionId = serviceAreaLoadingOptionIdByTypeId[type.id] || null;
    const adminProviders = (utilityProvidersByTypeId[type.id] || []).map((provider) => {
      const availableProviderMappedBy = item?.availableProviders.find(
        ({ id }) => id === provider.id,
      )?.mappedBy;

      return {
        ...provider,
        sublabel: lodashCompact([
          SOURCE_NAME_MAPPING[provider.source] || firstWordCapitalized(provider.source),
          availableProviderMappedBy ? ` (Mapped by ${availableProviderMappedBy})` : null,
        ]),
      };
    });

    const itemFields = !item
      ? {}
      : {
          item,
          utilityProvidersInRange: getProviderProps({
            providers: utilityProvidersInRange,
            mappedProviders: item.availableProviders || [],
            type: 'nearby',
          }),
          getServiceAreaFormFields: (formikBag) =>
            formFields.serviceArea.fields({
              item,
              addressId,
              utilityProvidersByAddress,
              isServiceAreaLoading,
              setServiceAreaLoading: (isLoading) => {
                setServiceAreaLoadingByTypeId((prevState) => ({
                  ...prevState,
                  [type.id]: isLoading,
                }));
              },
              serviceAreaLoadingOptionId,
              setServiceAreaLoadingOptionId: (optionId) => {
                setServiceAreaLoadingOptionIdByTypeId((prevState) => ({
                  ...prevState,
                  [type.id]: optionId,
                }));
              },
              updateProviderCoveragePoints,
              onMapServiceAreaProvider,
              formikBag,
            }),
          serviceAreaFormInitialState: formFields.serviceArea.initialState({ item }),
          getAdminFormFields: (formikBag) =>
            formFields.admin.fields({
              item,
              requiredTypes,
              utilityType: type,
              formikBag,
              adminProviders,
              utilityProvidersByAddress,
              collapseProvidersTypeId: collapseProviders[type.id],
              setCollapseProvidersByTypeId: (value) => {
                setCollapseProviders({
                  ...collapseProviders,
                  [type.id]: value,
                });
              },
              onConfirmMapping,
            }),
          adminFormInitialState: formFields.admin.initialState({ item, requiredTypes }),
          onAdminFormSubmit: formFields.admin.onSubmit({
            addressId,
            item,
            updateProviderCoveragePoints,
          }),
        };

    return {
      item,
      type,
      ...itemFields,
      ui: getActivationItemStatusUiState({
        label: type.label,
        utilityProviders: utilityProvidersByAddress,
        status: !item
          ? null
          : item.availableProviders.length > 0 && !requiredUtilityType?.needsMappingConfirmation
            ? UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED
            : UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER,
        completedStates: [UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED],
      }),
      collapsed: !collapsible ? false : collapsibleState[type.id],
      onRowClick: !collapsible
        ? null
        : () => {
            setCollapsibleState({
              ...collapsibleState,
              [type.id]: !collapsibleState[type.id],
            });
          },
    };
  });

  if (hideInactiveTypes) {
    sections = sections.filter(({ item }) => !!item);
  }

  return sections;
};

const SrUtilityProfitAdminProcessAddressForm = ({
  addressCoverage,
  utilityProvidersByTypeId,
  utilityTypes,
  updateProviderCoveragePoints,
  utilityProvidersInRangeByTypeId = {},
  utilityProvidersByAddressByTypeId = {},
  toggleRequiredType,
  onMapServiceAreaProvider,
  markAsRequiredEnabled,
  hideInactiveTypes,
  collapsible,
  onConfirmMapping,
}) => {
  const initialCollapseProviders = utilityTypes.reduce((acc, type) => {
    acc[type.id] = true;
    return acc;
  }, {});

  const initialCollapsibleState = utilityTypes.reduce(
    (acc, type) => ({
      ...acc,
      [type.id]: addressCoverage.items.some(
        (item) => item.type.id === type.id && item.availableProviders.length > 0,
      ),
    }),
    {},
  );

  const [collapseProviders, setCollapseProviders] = useState(initialCollapseProviders);
  const [collapsibleState, setCollapsibleState] = useState(initialCollapsibleState);
  const [upsertProviderModalArgs, setUpsertProviderModalArgs] = useState(null);
  const [upsertProviderError, setUpsertProviderError] = useState(null);
  const [serviceAreaLoadingByTypeId, setServiceAreaLoadingByTypeId] = useState({});
  const [serviceAreaLoadingOptionIdByTypeId, setServiceAreaLoadingOptionIdByTypeId] = useState({});
  const dispatch = useDispatch();

  const sections = getSections({
    addressId: addressCoverage.id,
    items: addressCoverage.items,
    requiredTypes: addressCoverage.requiredTypes,
    utilityProvidersByTypeId,
    utilityProvidersInRangeByTypeId,
    utilityProvidersByAddressByTypeId,
    updateProviderCoveragePoints,
    utilityTypes,
    collapseProviders,
    setCollapseProviders,
    onMapServiceAreaProvider,
    hideInactiveTypes,
    collapsible,
    collapsibleState,
    setCollapsibleState,
    onConfirmMapping,
    serviceAreaLoadingByTypeId,
    setServiceAreaLoadingByTypeId,
    serviceAreaLoadingOptionIdByTypeId,
    setServiceAreaLoadingOptionIdByTypeId,
  });

  const _handleProviderUpsert = ({ utilityType, currentUtilityProviderIds }) => {
    if (!utilityType || utilityType.id === UTILITY_TYPE_ID_INTERNET) {
      return;
    }
    setUpsertProviderError(null);
    setUpsertProviderModalArgs({
      utilityType,
      initialValues: {
        addressId: addressCoverage.id,
        utilityTypeId: utilityType.id,
      },
      addressDisplay: addressCoverage.display,
      onCancel: () => setUpsertProviderModalArgs(null),
      onSubmit: (utilityProviderUpsertForm) => {
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
          payload: utilityProviderUpsertForm,
          onSuccess: ({ utilityProvider }) => {
            setUpsertProviderModalArgs(null);
            updateProviderCoveragePoints({
              addressId: addressCoverage.id,
              utilityTypeId: utilityType.id,
              utilityProviderIds: lodashUniq([
                ...(currentUtilityProviderIds || []),
                utilityProvider.id,
              ]),
            });
          },
          onError: (error) => {
            setUpsertProviderError(error);
          },
        });
      },
      onRemoveCoverage: () => {
        setUpsertProviderModalArgs(null);
      },
    });
  };

  return (
    <div>
      {sections
        .filter(({ type }) => type.id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE)
        .map((section) => (
        <SrBox
          padding="23px 25px"
          borderTop="1px solid #e9ebec"
          key={section.type.id}
          id={`section-${section.type.label}`}
        >
          <SrFlex onClick={section.onRowClick} justify="between" cursor="pointer">
            <SrFlex columnGap="4px" align="center">
              <SrBox
                width="14px"
                height="14px"
                backgroundImage={`url("${section.ui.iconUrl}")`}
                backgroundSize="cover"
                marginRight="5px"
              />
              <SrText
                font="greycliff"
                size="18"
                weight="700"
                decoration={
                  section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
                    ? 'line-through'
                    : undefined
                }
                color={
                  section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
                    ? section.ui.color
                    : undefined
                }
              >
                {section.ui.label}
              </SrText>
              {section.ui.uiState !== UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED && (
                <SrBox
                  width="19px"
                  height="19px"
                  backgroundImage={`url("${section.type.iconUrl}")`}
                  backgroundSize="cover"
                />
              )}
            </SrFlex>
            {markAsRequiredEnabled && (
              <SrText
                font="greycliff"
                size="16"
                weight="600"
                color="blue300"
                cursor="pointer"
                onClick={() => toggleRequiredType(section.type.id)}
              >
                {`Mark as ${section.item ? 'Not ' : ''}Required`}
              </SrText>
            )}
          </SrFlex>
          {!section.collapsed && (
            <>
              {section.utilityProvidersInRange && section.utilityProvidersInRange.length > 0 && (
                <SrUtilityProfitAdminProcessAddressProviderListForm
                  title="Nearby Providers"
                  keyPrefix="nearby_provider"
                  providers={section.utilityProvidersInRange}
                  initialState={section.adminFormInitialState}
                  onSubmit={section.onAdminFormSubmit}
                  onProviderMapped={(provider, formikBag) => {
                    const selectedProviderIds = lodashUniq([
                      ...(formikBag.values.coverageUtilityProviderIds || []),
                      provider.id,
                    ]);
                    const providerCoveragePointConfig =
                      formikBag.values.coveragePointsConfigByProviderId[provider.id] || {};
                    const selectedVerificationMethods = lodashUniq([
                      ...(providerCoveragePointConfig.verificationMethods || []),
                      UTILITY_COVERAGE_POINT_VERIFICATION_METHOD_NEARBY_PROVIDER,
                    ]);
                    formikBag.setFieldValue('coverageUtilityProviderIds', selectedProviderIds);
                    formikBag.setFieldValue(
                      `coveragePointsConfigByProviderId[${provider.id}].verificationMethods`,
                      selectedVerificationMethods,
                    );
                    formikBag.validateForm().then(() => {
                      formikBag.handleSubmit();
                    });
                  }}
                  informationalComponent={(provider) => (
                    <SrText font="greycliff" fontSize="14px" lineHeight="17px" fontStyle="italic">
                      {provider.distanceInMiles} miles
                    </SrText>
                  )}
                />
              )}
              {section.item?.utilityProvidersInServiceAreas?.length > 0 && (
                <SrBox marginTop="20px" padding="0 22px">
                  <Formik
                    initialValues={section.serviceAreaFormInitialState}
                    enableReinitialize
                    render={(formikBag) => {
                      return (
                        <Form>
                          {section.getServiceAreaFormFields(formikBag).map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              margin="13px 0 25px"
                              formField={formField}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          ))}
                        </Form>
                      );
                    }}
                  />
                </SrBox>
              )}
              {section.item && (
                <SrBox padding="0 22px">
                  <Formik
                    initialValues={section.adminFormInitialState}
                    enableReinitialize
                    onSubmit={section.onAdminFormSubmit}
                    render={(formikBag) => {
                      return (
                        <Form>
                          <UPProviderSearchFormInner
                            addressDisplay={makeFullAddressDisplay(addressCoverage)}
                            utilityType={section.type}
                            onAddNew={({ formikBag: searchFormikBag }) => {
                              const currentUtilityProviderIds = [
                                ...(formikBag.values.coverageUtilityProviderIds || []),
                              ];
                              searchFormikBag.resetForm();
                              return _handleProviderUpsert({
                                utilityType: section.type,
                                currentUtilityProviderIds,
                              });
                            }}
                            onProviderSelected={(provider) => {
                              const selectedProviderIds = lodashUniq([
                                ...(formikBag.values.coverageUtilityProviderIds || []),
                                provider.id,
                              ]);
                              const nextValues = {
                                ...formikBag.values,
                                coverageUtilityProviderIds: selectedProviderIds,
                              };

                              formikBag.setFieldValue(
                                'coverageUtilityProviderIds',
                                selectedProviderIds,
                              );

                              return Promise.resolve(section.onAdminFormSubmit(nextValues));
                            }}
                          />
                          {section.getAdminFormFields(formikBag).map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              margin="13px 0 25px"
                              formField={formField}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          ))}
                          <SrButton
                            theme={SR_BUTTON_THEME_ADOBE_GREEN}
                            disabled={!formikBag.isValid || !formikBag.dirty}
                            width="326px"
                            margin="0 0 14px"
                            hasBoxShadow
                            type="submit"
                          >
                            Save
                          </SrButton>
                        </Form>
                      );
                    }}
                  />
                </SrBox>
              )}
            </>
          )}
        </SrBox>
      ))}
      {upsertProviderModalArgs && (
        <UPProviderUpsertForm {...{ ...upsertProviderModalArgs, error: upsertProviderError }} />
      )}
    </div>
  );
};

SrUtilityProfitAdminProcessAddressForm.propTypes = {
  addressCoverage: PropTypes.object.isRequired,
  utilityProvidersInRangeByTypeId: PropTypes.object,
  utilityProvidersByTypeId: PropTypes.object.isRequired,
  utilityTypes: PropTypes.array.isRequired,
  updateProviderCoveragePoints: PropTypes.func.isRequired,
  toggleRequiredType: PropTypes.func.isRequired,
  onMapServiceAreaProvider: PropTypes.func.isRequired,
  markAsRequiredEnabled: PropTypes.bool,
  hideInactiveTypes: PropTypes.bool,
  collapsible: PropTypes.bool,
};

SrUtilityProfitAdminProcessAddressForm.defaultProps = {
  markAsRequiredEnabled: true,
  hideInactiveTypes: false,
  collapsible: false,
};

SrUtilityProfitAdminProcessAddressForm.displayName = 'SrUtilityProfitAdminProcessAddressForm';

export default SrUtilityProfitAdminProcessAddressForm;
